// Customer Wallet page — balance + transaction history + optional cashback hint.
import React, { useEffect, useState } from 'react';
import { Wallet as WalletIcon, ArrowDownLeft, ArrowUpRight, Gift } from 'lucide-react';
import { api } from '../../lib/api';
import { useCustomerAuth } from '../../context/CustomerAuthContext';
import { useCurrency } from '../../context/CurrencyContext';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';

const KIND_META = {
  credit: { icon: ArrowDownLeft, color: 'text-emerald-600 bg-emerald-50' },
  debit: { icon: ArrowUpRight, color: 'text-red-600 bg-red-50' },
  refund: { icon: ArrowDownLeft, color: 'text-blue-600 bg-blue-50' },
  cashback: { icon: Gift, color: 'text-purple-600 bg-purple-50' },
};

const Wallet = () => {
  const { customer, loading: authLoading } = useCustomerAuth();
  const { formatPrice } = useCurrency();
  const [data, setData] = useState({ wallet: null, transactions: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!customer) { setLoading(false); return; }
    api.get('/customer/wallet')
      .then((r) => setData({ wallet: r.data.wallet, transactions: r.data.transactions }))
      .catch(() => setData({ wallet: null, transactions: [] }))
      .finally(() => setLoading(false));
  }, [customer]);

  if (!authLoading && !customer) {
    return <div className="p-10 text-center">Please sign in to view your wallet.</div>;
  }
  if (loading) return <div className="p-10 text-center text-gray-500">Loading wallet…</div>;

  const w = data.wallet || { balance: 0, cashback_balance: 0, refund_balance: 0 };

  return (
    <div className="bg-gray-50 dark:bg-gray-950 min-h-screen">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-6">My Wallet</h1>

        {/* Balance cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
          <Card data-testid="wallet-balance-card" className="bg-gradient-to-br from-red-600 to-red-800 text-white">
            <CardContent className="p-5">
              <WalletIcon className="h-5 w-5 mb-2 opacity-80" />
              <p className="text-xs uppercase tracking-wider opacity-80">Available Balance</p>
              <p className="text-3xl font-bold mt-1" data-testid="wallet-balance-value">{formatPrice(w.balance || 0)}</p>
            </CardContent>
          </Card>
          <Card data-testid="wallet-cashback-card">
            <CardContent className="p-5">
              <Gift className="h-5 w-5 mb-2 text-purple-600" />
              <p className="text-xs uppercase tracking-wider text-gray-500">Cashback</p>
              <p className="text-3xl font-bold mt-1 text-gray-900 dark:text-white">{formatPrice(w.cashback_balance || 0)}</p>
            </CardContent>
          </Card>
          <Card data-testid="wallet-refund-card">
            <CardContent className="p-5">
              <ArrowDownLeft className="h-5 w-5 mb-2 text-blue-600" />
              <p className="text-xs uppercase tracking-wider text-gray-500">Refunds (pending)</p>
              <p className="text-3xl font-bold mt-1 text-gray-900 dark:text-white">{formatPrice(w.refund_balance || 0)}</p>
            </CardContent>
          </Card>
        </div>

        {/* Transactions */}
        <Card>
          <CardContent className="p-0">
            <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-800">
              <h2 className="font-bold text-gray-900 dark:text-white">Transaction History</h2>
            </div>
            {data.transactions.length === 0 ? (
              <div className="p-10 text-center text-sm text-gray-500" data-testid="wallet-no-tx">No transactions yet. Your wallet activity will appear here.</div>
            ) : (
              <ul className="divide-y divide-gray-100 dark:divide-gray-800" data-testid="wallet-tx-list">
                {data.transactions.map((t) => {
                  const Meta = KIND_META[t.kind] || KIND_META.debit;
                  const sign = (t.kind === 'credit' || t.kind === 'refund' || t.kind === 'cashback') ? '+' : '-';
                  return (
                    <li key={t.id} data-testid={`wallet-tx-${t.id}`} className="px-5 py-3 flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${Meta.color}`}><Meta.icon className="h-4 w-4" /></div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-900 dark:text-white text-sm truncate">{t.description || t.kind}</p>
                        <p className="text-[11px] text-gray-500">{new Date(t.created_at).toLocaleString('en-IN')}{t.reference ? ` · ${t.reference}` : ''}</p>
                      </div>
                      <div className="text-right">
                        <p className={`font-bold ${sign === '+' ? 'text-emerald-600' : 'text-red-600'}`}>{sign}{formatPrice(t.amount)}</p>
                        <Badge className="text-[9px] uppercase">{t.kind}</Badge>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Wallet;
